import * as Sentry from '@sentry/vue';
import { computed, ref, watch, type Ref } from 'vue';
import { storeToRefs } from 'pinia';
import {
  bulkApiErrorMessage,
  fetchBulkRun,
  isBulkRunActive,
  isBulkRunFinished,
  postBulkCancel,
  postBulkExecute,
  postBulkPreview,
  readBulkApiError,
  type BulkOperation,
  type BulkRequestSelection,
  type BulkRun,
} from '@/composables/api/useBulkActionApi';
import { useBulkActionRunStore, type PendingBulkActionRun } from '@/stores/bulkActionRunStore';
import type { SchedulesBeforeRun } from '@/composables/api/bulkActionCachePatch';

/** The two kinds of row the custom list search can tick. */
export type MixedRunKind = 'lists' | 'schedules';

/** One of the two runs of an action on lists and schedules, before it is previewed. */
export interface MixedRunPart {
  kind: MixedRunKind;
  entitySlug: string;
  selection: BulkRequestSelection;
  operation: BulkOperation;
  /** Display labels for the values the user picked. They are not sent to the server. */
  valueLabels?: Record<string, string>;
  /** The confirmation's lines for a bulk edit, as the edit dialog words them. */
  summary?: string[];
}

export interface PreviewedMixedRunPart extends MixedRunPart {
  run: BulkRun;
}

/** What the coordinator needs of the bar's run tracker (`useBulkActionRun`). */
export interface MixedRunTracker {
  run: Readonly<Ref<BulkRun | null>>;
  start: (
    executedRun: BulkRun,
    valueLabels?: Record<string, string>,
    previousRun?: BulkRun | null,
    schedulesBeforeRun?: SchedulesBeforeRun,
  ) => void;
  /** The cached schedules as they read now, for a schedule run. Taken before each execute request. */
  snapshotBeforeExecute: (entitySlug: string) => SchedulesBeforeRun | undefined;
}

export type MixedRunPhase = 'idle' | 'checking' | 'ready' | 'count_changed' | 'failed' | 'saving';

/** A 409 on one part's preview: that part's search now matches a different number of rows. */
export interface MixedRunCountChange {
  kind: MixedRunKind;
  message: string;
  currentCount: number;
}

const KIND_NOUNS: Record<MixedRunKind, { singular: string; plural: string }> = {
  lists: { singular: 'list', plural: 'lists' },
  schedules: { singular: 'schedule', plural: 'schedules' },
};

const ACTION_VERBS: Record<BulkOperation['action'], string> = {
  update: 'Change',
  delete: 'Delete',
  add_tag: 'Add tags to',
  remove_tag: 'Remove tags from',
};

const PAST_TENSE: Record<BulkOperation['action'], string> = {
  update: 'changed',
  delete: 'deleted',
  add_tag: 'tagged',
  remove_tag: 'untagged',
};

function counted(count: number, kind: MixedRunKind): string {
  const nouns = KIND_NOUNS[kind];
  return `${count.toLocaleString()} ${count === 1 ? nouns.singular : nouns.plural}`;
}

function kindForEntity(entitySlug: string): MixedRunKind {
  return entitySlug === 'location_zone_custom_playlist' ? 'lists' : 'schedules';
}

/**
 * The confirmation's one sentence, for example "Delete 3 lists (with 27 schedules) and 14
 * schedules". The number in brackets is the list preview's `delete_impact`: the schedules that
 * go with the deleted lists. A run with nothing to change is left out of the sentence, because it
 * will not be executed. When neither run would change anything, it says so.
 */
export function mixedConfirmationText(previews: PreviewedMixedRunPart[]): string {
  const changing = previews.filter((part) => part.run.counts.will_change > 0);
  const first = changing[0];
  if (!first) return 'Nothing would change.';
  const phrases = changing.map((part) => {
    const phrase = counted(part.run.counts.will_change, part.kind);
    const impact = part.operation.action === 'delete' ? part.run.delete_impact : null;
    return impact && impact.count > 0 ? `${phrase} (with ${counted(impact.count, 'schedules')})` : phrase;
  });
  return `${ACTION_VERBS[first.operation.action]} ${phrases.join(' and ')}`;
}

/**
 * Run one bulk action on the ticked lists and the ticked schedules of the custom list search.
 *
 * The server runs one entity per run, so the action is two runs on the existing endpoints.
 * - Both are previewed together. If either preview fails, the other preview is cancelled with
 *   the cancel endpoint, and the message names the one that failed.
 * - The confirmation is one sentence for both (`mixedConfirmationText`).
 * - The schedule run is executed first. The list run is executed only once the schedule run is
 *   final, because the server refuses a second active run (`another_run_active`). The waiting
 *   list run is kept in `bulkActionRunStore`, so a reload still starts it.
 * - A run whose preview has nothing to change is never executed, since execute would answer
 *   422 `nothing_to_change`. Its preview is cancelled and the other run starts straight away.
 *   For example, deleting list A and one schedule on list A leaves the schedule run empty,
 *   because `without_lists` drops the schedule. Only list A's run is executed.
 * - If the list run's preview has expired by the time the schedule run ends, the bar says so
 *   and offers to run it again. Running it again previews the same selection afresh.
 * - A 409 on either preview means that part's "all matching" count went stale. The user confirms
 *   the new count (`confirmNewCount`), which becomes that part's `expected_count`, and both are
 *   previewed again. A 422 over the cap is set on `refusal` for the bar to show, as the single
 *   run does.
 */
export function useMixedBulkRun(tracker: MixedRunTracker) {
  const store = useBulkActionRunStore();
  const { pendingRun } = storeToRefs(store);

  const phase = ref<MixedRunPhase>('idle');
  const previews = ref<PreviewedMixedRunPart[]>([]);
  const errorMessage = ref<string | null>(null);
  /** The 409's message and new total, while the dialog asks again. */
  const countChange = ref<MixedRunCountChange | null>(null);
  /** The server's message when it refused a selection outright, for example over the 1,000 cap. */
  const refusal = ref<string | null>(null);
  /** What the bar says about the waiting run when it could not start, such as an expired preview. */
  const nextRunMessage = ref<string | null>(null);
  const canRunAgain = ref(false);

  let lastParts: MixedRunPart[] = [];
  /** Previewed runs that were executed or handed to the store. Closing the dialog leaves them alone. */
  const handedOffRunIds = new Set<number>();
  /** Bumped by every preview and every cancel, so a late answer is cancelled rather than shown. */
  let previewCount = 0;
  let executingPending = false;

  function cancelPreviewedRun(entitySlug: string, run: BulkRun): void {
    if (run.status !== 'previewed' || handedOffRunIds.has(run.id)) return;
    // Nothing was written, and the preview expires on its own after 15 minutes. So a failed
    // cancel only leaves a harmless previewed row behind.
    postBulkCancel(entitySlug, run.id).catch((error) => {
      Sentry.captureException(error);
    });
  }

  async function preview(parts: MixedRunPart[]): Promise<void> {
    const thisPreview = ++previewCount;
    lastParts = parts;
    phase.value = 'checking';
    previews.value = [];
    errorMessage.value = null;
    countChange.value = null;
    refusal.value = null;
    const results = await Promise.allSettled(
      parts.map((part) => postBulkPreview(part.entitySlug, part.selection, part.operation)),
    );
    const previewedParts = parts.flatMap((part, index) => {
      const result = results[index]!;
      return result.status === 'fulfilled' ? [{ ...part, run: result.value }] : [];
    });
    if (thisPreview !== previewCount) {
      for (const part of previewedParts) cancelPreviewedRun(part.entitySlug, part.run);
      return;
    }
    const failedIndex = results.findIndex((result) => result.status === 'rejected');
    if (failedIndex >= 0) {
      for (const part of previewedParts) cancelPreviewedRun(part.entitySlug, part.run);
      const errors = results.map((result) => (result.status === 'rejected' ? readBulkApiError(result.reason) : null));
      const capRefusal = errors.find((error) => error?.status === 422 && error.body.errors?.selection?.[0]);
      if (capRefusal) {
        refusal.value = capRefusal.body.errors!.selection![0]!;
        phase.value = 'idle';
        return;
      }
      const countChangedIndex = errors.findIndex(
        (error) => error?.status === 409 && error.body.code === 'count_changed' && typeof error.body.current_count === 'number',
      );
      if (countChangedIndex >= 0) {
        const { body } = errors[countChangedIndex]!;
        const currentCount = body.current_count as number;
        countChange.value = {
          kind: parts[countChangedIndex]!.kind,
          message: body.message ?? `The search now matches ${currentCount.toLocaleString()} rows.`,
          currentCount,
        };
        phase.value = 'count_changed';
        return;
      }
      const failure = results[failedIndex] as PromiseRejectedResult;
      const failedKind = parts[failedIndex]!.kind;
      const reason = bulkApiErrorMessage(failure.reason, 'Try again.');
      const cancelledNote = previewedParts
        .map((part) => ` The check of the ${KIND_NOUNS[part.kind].plural} was cancelled, so nothing will change.`)
        .join('');
      errorMessage.value = `Couldn't check the ${KIND_NOUNS[failedKind].plural}. ${reason}${cancelledNote}`;
      phase.value = 'failed';
      return;
    }
    previews.value = previewedParts;
    phase.value = 'ready';
  }

  function retry(): Promise<void> {
    return preview(lastParts);
  }

  /** Confirm again after a 409: the new total becomes that part's `expected_count`. */
  function confirmNewCount(): Promise<void> {
    const change = countChange.value;
    if (!change) return Promise.resolve();
    return preview(
      lastParts.map((part) =>
        part.kind === change.kind && part.selection.mode !== 'ids'
          ? { ...part, selection: { ...part.selection, expected_count: change.currentCount } }
          : part,
      ),
    );
  }

  /** The user closed the confirmation. Every preview not yet executed is cancelled. */
  function cancel(): void {
    previewCount++;
    for (const part of previews.value) cancelPreviewedRun(part.entitySlug, part.run);
    previews.value = [];
    countChange.value = null;
    refusal.value = null;
    phase.value = 'idle';
  }

  const confirmationText = computed(() => mixedConfirmationText(previews.value));
  const canExecute = computed(
    () => phase.value === 'ready' && previews.value.some((part) => part.run.counts.will_change > 0),
  );

  /**
   * Execute the confirmed action. Returns the kinds it covered, including a kind whose run was
   * skipped for having nothing to change, or null when nothing was started.
   */
  async function execute(): Promise<MixedRunKind[] | null> {
    if (!canExecute.value) return null;
    // Schedules first. The list run waits for the schedule run to be final.
    const toRun = previews.value
      .filter((part) => part.run.counts.will_change > 0)
      .sort((left, right) => (left.kind === right.kind ? 0 : left.kind === 'schedules' ? -1 : 1));
    const [firstPart, secondPart] = toRun;
    if (!firstPart) return null;
    // Read before the request. A cancel that lands meanwhile empties `previews`.
    const confirmedParts = previews.value;
    const coveredKinds = confirmedParts.map((part) => part.kind);
    // Marked before the request, so a cancel that lands while it is in flight leaves both alone.
    const handedOffParts = secondPart ? [firstPart, secondPart] : [firstPart];
    for (const part of handedOffParts) handedOffRunIds.add(part.run.id);
    phase.value = 'saving';
    errorMessage.value = null;
    let executedRun: BulkRun;
    // Taken before the request. A live poll that lands while it is in flight may already hold
    // shifted dates, and a shift must start from the dates the user confirmed.
    const schedulesBeforeRun = tracker.snapshotBeforeExecute(firstPart.entitySlug);
    try {
      executedRun = await postBulkExecute(firstPart.entitySlug, firstPart.run.id);
    } catch (error) {
      for (const part of handedOffParts) handedOffRunIds.delete(part.run.id);
      const { status, body } = readBulkApiError(error);
      if (status === 410 || body.code === 'preview_expired') {
        // Both previews were made together, so both have expired. Nothing is cancelled: a cancel
        // of an expired preview answers 409, and the previews are gone on their own already.
        await retry();
        // retry() moved the phase on. When the check failed, its own error is what the user needs.
        if ((phase.value as MixedRunPhase) === 'ready') {
          errorMessage.value = 'That check was more than 15 minutes old, so the rows were checked again. Confirm the new counts.';
        }
        return null;
      }
      errorMessage.value = bulkApiErrorMessage(error, "Couldn't start the bulk action. Try again.");
      phase.value = 'ready';
      return null;
    }
    for (const part of confirmedParts) {
      if (part.run.counts.will_change === 0) cancelPreviewedRun(part.entitySlug, part.run);
    }
    nextRunMessage.value = null;
    canRunAgain.value = false;
    if (secondPart) {
      store.setPending({
        runId: secondPart.run.id,
        afterRunId: executedRun.id,
        entity: secondPart.entitySlug,
        selection: secondPart.selection,
        operation: secondPart.operation,
        valueLabels: secondPart.valueLabels,
      });
    } else {
      store.clearPending();
    }
    tracker.start(executedRun, firstPart.valueLabels, undefined, schedulesBeforeRun);
    previews.value = [];
    phase.value = 'idle';
    return coveredKinds;
  }

  /** The kind of the run waiting for the tracked run to finish, such as `lists`. */
  const waitingKind = computed<MixedRunKind | null>(() =>
    pendingRun.value && tracker.run.value?.id === pendingRun.value.afterRunId ? kindForEntity(pendingRun.value.entity) : null,
  );

  function waitingNoun(pending: PendingBulkActionRun): string {
    return KIND_NOUNS[kindForEntity(pending.entity)].plural;
  }

  /**
   * The waiting run is no longer previewed. Another tab reloaded during the same schedule run and
   * executed it first. When that run is queued, running or finished, this tab follows it, so it
   * does not report the lists as not deleted. Returns false when there is nothing to follow, for
   * example when the run was cancelled.
   */
  async function followRunStartedElsewhere(pending: PendingBulkActionRun, finishedRun: BulkRun): Promise<boolean> {
    let startedRun: BulkRun;
    try {
      startedRun = await fetchBulkRun(pending.runId);
    } catch (error) {
      Sentry.captureException(error);
      return false;
    }
    if (!isBulkRunActive(startedRun.status) && !isBulkRunFinished(startedRun.status)) return false;
    store.clearPending();
    tracker.start(startedRun, pending.valueLabels, finishedRun, undefined);
    return true;
  }

  async function executePending(pending: PendingBulkActionRun, finishedRun: BulkRun): Promise<void> {
    executingPending = true;
    try {
      const schedulesBeforeRun = tracker.snapshotBeforeExecute(pending.entity);
      const executedRun = await postBulkExecute(pending.entity, pending.runId);
      store.clearPending();
      tracker.start(executedRun, pending.valueLabels, finishedRun, schedulesBeforeRun);
    } catch (error) {
      const { status, body } = readBulkApiError(error);
      if (status === 409 && body.code === 'run_not_previewed' && (await followRunStartedElsewhere(pending, finishedRun))) {
        return;
      }
      if (status === 410 || body.code === 'preview_expired') {
        // The store keeps the waiting run, so "Run it again" can preview the same selection.
        nextRunMessage.value = `The ${waitingNoun(pending)} were not ${PAST_TENSE[pending.operation.action]}. Their check was more than 15 minutes old by the time the ${KIND_NOUNS[kindForEntity(finishedRun.entity)].plural} finished.`;
        canRunAgain.value = true;
        return;
      }
      Sentry.captureException(error);
      nextRunMessage.value = `The ${waitingNoun(pending)} were not ${PAST_TENSE[pending.operation.action]}. ${bulkApiErrorMessage(error, 'Try again from the search.')}`;
      store.clearPending();
    } finally {
      executingPending = false;
    }
  }

  // Once the tracked run is final, start the run waiting for it. This also fires after a reload,
  // when the tracker loads a finished run and the store still holds the waiting one.
  watch(
    () => [tracker.run.value?.id, tracker.run.value?.status, pendingRun.value?.runId] as const,
    () => {
      const pending = pendingRun.value;
      const current = tracker.run.value;
      if (!pending || !current || current.id !== pending.afterRunId) return;
      if (!isBulkRunFinished(current.status) || executingPending || canRunAgain.value) return;
      void executePending(pending, current);
    },
    { immediate: true },
  );

  /** Preview the waiting run's selection again, and execute it when something would change. */
  async function runAgain(): Promise<void> {
    const pending = pendingRun.value;
    const finishedRun = tracker.run.value;
    if (!pending || !finishedRun) return;
    canRunAgain.value = false;
    nextRunMessage.value = null;
    executingPending = true;
    try {
      const previewedRun = await postBulkPreview(pending.entity, pending.selection, pending.operation);
      if (previewedRun.counts.will_change === 0) {
        cancelPreviewedRun(pending.entity, previewedRun);
        store.clearPending();
        nextRunMessage.value = `Nothing in the ${waitingNoun(pending)} would change now.`;
        return;
      }
      const schedulesBeforeRun = tracker.snapshotBeforeExecute(pending.entity);
      const executedRun = await postBulkExecute(pending.entity, previewedRun.id);
      store.clearPending();
      tracker.start(executedRun, pending.valueLabels, finishedRun, schedulesBeforeRun);
    } catch (error) {
      Sentry.captureException(error);
      store.clearPending();
      nextRunMessage.value = `The ${waitingNoun(pending)} were not ${PAST_TENSE[pending.operation.action]}. ${bulkApiErrorMessage(error, 'Try again from the search.')}`;
    } finally {
      executingPending = false;
    }
  }

  /** The user dismissed the finished run. A run still waiting is dropped, and its preview cancelled. */
  function dismissPending(): void {
    const pending = pendingRun.value;
    if (pending) {
      postBulkCancel(pending.entity, pending.runId).catch((error) => {
        // A preview that expired, or was never there, cannot be cancelled. That is harmless.
        if (readBulkApiError(error).status !== 409) Sentry.captureException(error);
      });
    }
    store.clearPending();
    nextRunMessage.value = null;
    canRunAgain.value = false;
  }

  return {
    phase,
    previews,
    errorMessage,
    countChange,
    refusal,
    confirmationText,
    canExecute,
    preview,
    retry,
    confirmNewCount,
    cancel,
    execute,
    waitingKind,
    nextRunMessage,
    canRunAgain,
    runAgain,
    dismissPending,
  };
}

export type MixedBulkRun = ReturnType<typeof useMixedBulkRun>;
